/**
 * AudioService.js
 * Handles audio recording and transcription using the Voxtral API
 */

import { API_URL } from './constants';

/**
 * Supported audio formats for transcription
 */
export const SUPPORTED_AUDIO_FORMATS = ['mp3', 'wav', 'm4a', 'webm', 'ogg', 'flac'];

/**
 * Get the best supported MIME type for recording
 * @returns {string} The MIME type to use for MediaRecorder
 */
export const getSupportedMimeType = () => {
    const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg',
    ];

    for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
            return mimeType;
        }
    }

    return ''; // Let browser choose default
};

/**
 * Get file extension from MIME type
 * @param {string} mimeType - The MIME type of the audio
 * @returns {string} The file extension
 */
export const getExtensionFromMimeType = (mimeType) => {
    if (!mimeType) return 'webm';

    if (mimeType.includes('mp4') || mimeType.includes('m4a')) return 'm4a';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('wav')) return 'wav';
    if (mimeType.includes('flac')) return 'flac';
    if (mimeType.includes('mp3') || mimeType.includes('mpeg')) return 'mp3';

    return 'webm'; // Default for webm/opus
};

/**
 * Start audio recording
 * @param {Object} options - Recording options
 * @param {React.MutableRefObject} options.mediaRecorderRef - Ref to store MediaRecorder instance
 * @param {React.MutableRefObject} options.audioChunksRef - Ref to store audio chunks
 * @param {Function} options.onStart - Callback when recording starts
 * @param {Function} options.onStop - Callback when recording stops (receives audioBlob)
 * @param {Function} options.onError - Callback for errors
 * @returns {Promise<MediaStream>} The audio stream
 */
export const startRecording = async ({
    mediaRecorderRef,
    audioChunksRef,
    onStart,
    onStop,
    onError
}) => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mimeType = getSupportedMimeType();

        const mediaRecorder = new MediaRecorder(
            stream,
            mimeType ? { mimeType } : {}
        );

        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                audioChunksRef.current.push(event.data);
            }
        };

        mediaRecorder.onstop = async () => {
            // Stop all tracks to release microphone
            stream.getTracks().forEach(track => track.stop());

            if (audioChunksRef.current.length === 0) {
                onError?.(new Error('No audio recorded.'));
                return;
            }

            // Create audio blob
            const audioBlob = new Blob(audioChunksRef.current, {
                type: mediaRecorder.mimeType || 'audio/webm'
            });

            onStop?.(audioBlob);
        };

        mediaRecorder.onerror = (event) => {
            console.error('MediaRecorder error:', event.error);
            stream.getTracks().forEach(track => track.stop());
            onError?.(event.error || new Error('Recording error occurred.'));
        };

        // Start recording - collect data every second
        mediaRecorder.start(1000);
        onStart?.();

        return stream;
    } catch (error) {
        console.error('Error accessing microphone:', error);
        throw error;
    }
};

/**
 * Stop audio recording
 * @param {React.MutableRefObject} mediaRecorderRef - Ref containing MediaRecorder instance
 */
export const stopRecording = (mediaRecorderRef) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
    }
};

/**
 * Get user-friendly error message for microphone errors
 * @param {Error} error - The error object
 * @returns {string} User-friendly error message
 */
export const getMicrophoneErrorMessage = (error) => {
    if (error.name === 'NotAllowedError') {
        return 'Microphone access denied. Please allow microphone access in your browser settings.';
    }
    if (error.name === 'NotFoundError') {
        return 'No microphone found. Please connect a microphone.';
    }
    if (error.name === 'NotReadableError') {
        return 'Microphone is already in use by another application.';
    }
    return `Error accessing microphone: ${error.message}`;
};

/**
 * Transcribe audio using the Voxtral API
 * @param {Blob} audioBlob - The audio blob to transcribe
 * @param {Object} options - Transcription options
 * @param {string} options.userId - User ID for the request
 * @param {string} options.accessToken - Access token for authentication
 * @param {string} options.language - Optional language code for transcription
 * @returns {Promise<Object>} Transcription result with text, language, and model
 */
export const transcribeAudio = async (audioBlob, { userId, accessToken, language } = {}) => {
    const formData = new FormData();

    // Determine file extension based on MIME type
    const extension = getExtensionFromMimeType(audioBlob.type);

    formData.append('audio', audioBlob, `recording.${extension}`);

    if (language) {
        formData.append('language', language);
    }

    const headers = {};
    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${API_URL}/api/transcribe`, {
        method: 'POST',
        headers,
        body: formData,
    });

    if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Transcription failed with status ${response.status}`;

        try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.error || errorMessage;
        } catch {
            if (errorText) {
                errorMessage = errorText;
            }
        }

        throw new Error(errorMessage);
    }

    const result = await response.json();
    return result;
};

/**
 * Custom hook logic for audio recording and transcription
 * This provides the core functionality that can be used in components
 */
export const createAudioRecordingHandler = ({
    mediaRecorderRef,
    audioChunksRef,
    isRecording,
    setIsRecording,
    setIsTranscribing,
    user,
    onTranscriptionComplete,
    showNotification
}) => {
    /**
     * Toggle recording on/off
     */
    const toggleRecording = async () => {
        if (isRecording) {
            // Stop recording
            stopRecording(mediaRecorderRef);
            setIsRecording(false);
            return;
        }

        // Start recording
        try {
            await startRecording({
                mediaRecorderRef,
                audioChunksRef,
                onStart: () => {
                    setIsRecording(true);
                    showNotification?.('info', 'Recording started... Click again to stop.');
                },
                onStop: async (audioBlob) => {
                    setIsRecording(false);
                    await handleTranscription(audioBlob);
                },
                onError: (error) => {
                    setIsRecording(false);
                    showNotification?.('error', error.message || 'Recording error occurred.');
                }
            });
        } catch (error) {
            setIsRecording(false);
            showNotification?.('error', getMicrophoneErrorMessage(error));
        }
    };

    /**
     * Handle transcription of recorded audio
     * @param {Blob} audioBlob - The recorded audio blob
     */
    const handleTranscription = async (audioBlob) => {
        setIsTranscribing(true);

        try {
            const result = await transcribeAudio(audioBlob, {
                userId: user?.uid || 'anonymous',
                accessToken: user?.accessToken,
            });

            if (result.text) {
                onTranscriptionComplete?.(result.text);
                showNotification?.('success', 'Audio transcribed successfully!');
            } else {
                showNotification?.('warning', 'No speech detected in the recording.');
            }
        } catch (error) {
            console.error('Transcription error:', error);
            showNotification?.('error', `Transcription failed: ${error.message}`);
        } finally {
            setIsTranscribing(false);
        }
    };

    return { toggleRecording };
};
